import * as React from 'react';

interface Printer {
    printerId: string;
    description: string;
    station: string;
    queueEnabled: boolean;
}

interface PrinterPickerModalProps {
    show: boolean;
    /** What's being printed — shown in the modal title */
    title: string;
    onPrint: (printerId: string) => void;
    onPreview: () => void;
    onClose: () => void;
}

export const PrinterPickerModal: React.FC<PrinterPickerModalProps> = ({ show, title, onPrint, onPreview, onClose }) => {
    const [printers, setPrinters] = React.useState<Printer[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [selectedPrinterId, setSelectedPrinterId] = React.useState<string>('');
    const [printing, setPrinting] = React.useState(false);

    React.useEffect(() => {
        if (show && printers.length === 0) {
            setLoading(true);
            fetch('/api/sales/guest-vouchers/printers')
                .then(r => r.json())
                .then((data: Printer[]) => {
                    setPrinters(data);
                    if (data.length > 0) {
                        // Default to first queue-enabled printer, or first printer
                        const defaultPrinter = data.find(p => p.queueEnabled) ?? data[0];
                        setSelectedPrinterId(defaultPrinter.printerId);
                    }
                })
                .catch(() => setPrinters([]))
                .finally(() => setLoading(false));
        }
    }, [show]);

    const handlePrint = async () => {
        if (!selectedPrinterId) return;
        setPrinting(true);
        try {
            await onPrint(selectedPrinterId);
        } finally {
            setPrinting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-sm">
                <div className="modal-content">
                    <div className="modal-header py-2">
                        <h6 className="modal-title">{title}</h6>
                        <button type="button" className="btn-close btn-close-sm" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {loading && (
                            <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm" role="status"></div>
                                <span className="ms-2 text-muted">Loading printers...</span>
                            </div>
                        )}

                        {!loading && printers.length === 0 && (
                            <div className="text-muted text-center py-2">No printers configured.</div>
                        )}

                        {!loading && printers.length > 0 && (
                            <div>
                                <label className="form-label small fw-semibold">Select Printer</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={selectedPrinterId}
                                    onChange={e => setSelectedPrinterId(e.target.value)}
                                >
                                    {printers.map(p => (
                                        <option key={p.printerId} value={p.printerId}>
                                            {p.description}{p.station ? ` (${p.station})` : ''}{p.queueEnabled ? '' : ' [disabled]'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer py-2 d-flex justify-content-between">
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => { onPreview(); onClose(); }}
                        >
                            Preview Only
                        </button>
                        <div className="d-flex gap-2">
                            <button className="btn btn-sm btn-secondary" onClick={onClose}>Cancel</button>
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={handlePrint}
                                disabled={!selectedPrinterId || printing || loading}
                            >
                                {printing ? 'Printing...' : 'Print'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
